'use client';

import React, { useState } from 'react';
import { Grid, CalloutCard, BlockStack, Text, Box } from '@shopify/polaris';
import EmailExample from '@/public/assets/email-abandonment-flow-example-1.png';
import ShopExample from '@/public/assets/shop-videos.jpg';
import WebsiteExample from '@/public/assets/product-page-stories.jpg';
import PublishEmail from '@/features/publish/PublishEmail';
import PublishShop from '@/features/publish/PublishShop';
import PublishWebsite from '@/features/publish/PublishWebsite';
import { Modal, TitleBar } from '@shopify/app-bridge-react';
import usePageNavigation from '@/hooks/usePageNavigation';
import { Page } from '@/components/Page';
import { PUBLISH } from '@/constants/text';
import { useUserDataStore } from '@/stores/userDataStore';
enum PublishType {
  EMAIL = 'email',
  SHOP = 'shop',
  WEBSITE = 'website',
}

const PUBLISH_LABELS = {
  [PublishType.EMAIL]: 'Connect email',
  [PublishType.SHOP]: 'Connect shop',
  [PublishType.WEBSITE]: 'Connect Tolstoy',
};

const PublishPage: React.FC = () => {
  const { isLoggedIn } = useUserDataStore();
  const [activeModal, setActiveModal] = useState<PublishType | null>(null);

  const getActionProps = (type: PublishType) => {
    if (!isLoggedIn) {
      return {
        content: '...',
        loading: true,
        disabled: true,
      };
    }

    return {
      content: PUBLISH_LABELS[type],
      onAction: () => openModal(type),
    };
  };

  const openModal = (modal: PublishType) => {
    setActiveModal(modal);
  };

  const closeModal = () => {
    setActiveModal(null);
  };

  const renderModalContent = () => {
    switch (activeModal) {
      case PublishType.EMAIL:
        return <PublishEmail />;
      case PublishType.SHOP:
        return <PublishShop />;
      case PublishType.WEBSITE:
        return <PublishWebsite />;
      default:
        return null;
    }
  };

  const { navigateBack, previousPageTitle } = usePageNavigation();

  return (
    <Page backAction={{ content: previousPageTitle, onAction: navigateBack }} title={PUBLISH}>
      <BlockStack gap="500">
        <Text as="h1" variant="headingSm">
          Free for Bunu
        </Text>
        <Grid columns={{ xs: 1, sm: 1, md: 2, lg: 2, xl: 2 }}>
          <Grid.Cell>
            <CalloutCard
              title={`Publish to ${PublishType.WEBSITE}`}
              illustration={WebsiteExample.src}
              primaryAction={getActionProps(PublishType.WEBSITE)}
            >
              <p>Publish your AI videos on your website, completely free.</p>
            </CalloutCard>
          </Grid.Cell>

          <Grid.Cell>
            <CalloutCard
              title={`Publish to ${PublishType.EMAIL}`}
              illustration={EmailExample.src}
              primaryAction={getActionProps(PublishType.EMAIL)}
            >
              <p>Automatically display your product videos in any email flow, free of charge.</p>
            </CalloutCard>
          </Grid.Cell>

          <Grid.Cell>
            <CalloutCard
              title={`Publish to ${PublishType.SHOP}`}
              illustration={ShopExample.src}
              primaryAction={getActionProps(PublishType.SHOP)}
            >
              <p>Feature your videos to millions of Shop app users, for free.</p>
            </CalloutCard>
          </Grid.Cell>
        </Grid>
      </BlockStack>

      <Modal open={!!activeModal} onHide={closeModal} variant="large">
        <TitleBar title={`Publish to ${activeModal}`} />
        <Box padding="400">{renderModalContent()}</Box>
      </Modal>
    </Page>
  );
};

export default PublishPage;
